import React from 'react';
import { FormHelperText, SxProps, TextField, Theme,  } from '@mui/material';

export type ErrorStatus =
  | { tag: 'Submited'; errors: string[] }
  | { tag: 'Not submitted' };

type HelperVariant = 'warning' | 'info';

type TextInputProps = {
  defaultValue?: string | number;
  onChange: (value: string) => void;
  value?: string;
  label: string;
  name: string;
  placeholder?: string;
  errors?: ErrorStatus;
  required?: boolean;
  sx?: SxProps<Theme>;
  multiline?: boolean;
  autoComplete?: 'false' | 'true';
  disabled?: boolean;
  helperText?: string;
  helperVariant?: HelperVariant;
};

/**
 * TextInput
 *
 * A controlled text input component based on MUI's `TextField`.
 * It handles value changes via the `onChange` callback and displays validation errors
 *
 * @param label - displayed above the text input field.
 * @param placeholder - displayed inside the input when empty.
 * @param onChange - on every value change.
 * @param required - if the input is required.
 * @param errors - Error status object used to display validation messages after form submission.
 * @param defaultValue - The default value of the text input.
 * @param name - The name attribute of the input.
 * @param multiline - Enables multi-line input mode if true.
 * @param sx - Optional MUI `sx` style overrides for custom styling.
 * @param helperText - Text, that we show to user with information, when we don't have errors
 * @param helperVariant - Type and style for helperText, declared in clear-theme.ts
 */

function TextInput({
  label,
  placeholder,
  value,
  onChange,
  required = false,
  errors,
  defaultValue,
  name,
  multiline,
  sx,
  autoComplete,
  disabled = false,
  helperText,
  helperVariant = 'warning'
}: TextInputProps) {  
  const isError = errors?.tag === 'Submited' && errors.errors?.length > 0;
  const errorMessage = isError ? errors.errors.join(', ') : '';  

  return (
    <>
      <TextField
        value={value}
        id="outlined-controlled"
        //we use required just for visibility, if it's not correct,
        // we will catch this error in validator of component 
        label={required ? `${label} *` : label}
        onChange={(e) => onChange(e.target.value)}
        type="text"        
        variant="outlined"
        fullWidth
        error={isError}        
        defaultValue={defaultValue}
        name={name}
        sx={sx}
        multiline={multiline}
        autoComplete="off"
        disabled={disabled}      
        helperText={isError ? errorMessage : undefined}      
      />

      {!isError && helperText && (
        <FormHelperText variant={helperVariant} sx={{ ml: 1 , mb: '10px'}}>
          {helperText}
        </FormHelperText>
      )}
    </>
  );
}

export default TextInput;
